import React, { useMemo, useState, useRef, useEffect } from "react";
import { FlightDetails, ConnectionFlight, FlightSegment } from "./types";
import { postFlightRequest } from "apis/loadsServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import DefaultAirLogo from "../../assets/images/logo.svg";
import { formatDateTime } from "utils/dateUtils";
import { fetchUserCreditBalance } from "apis/creditService";

// Skeleton Loader Component
const SkeletonBox = ({ width = "100%", height = "1rem", className = "" }: { width?: string | number; height?: string | number; className?: string }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} style={{ width, height }} />
);

const FlightCardSkeleton = () => (
  <div className="bg-white rounded-lg p-5 border border-gray-200">
    <div className="flex items-center justify-between gap-4 mb-4">
      <div className="flex items-center gap-3">
        <SkeletonBox width="48px" height="48px" className="rounded" />
        <div>
          <SkeletonBox width="60px" height="12px" className="mb-2" />
          <SkeletonBox width="100px" height="14px" />
        </div>
      </div>
      <div>
        <SkeletonBox width="70px" height="12px" className="mb-2" />
        <SkeletonBox width="80px" height="14px" />
      </div>
    </div>
    <div className="bg-lightblue-100 p-4 rounded-lg flex items-center gap-6">
      <div className="flex flex-col items-start gap-1 min-w-[90px]">
        <SkeletonBox width="50px" height="14px" />
        <SkeletonBox width="80px" height="12px" className="mt-1" />
        <SkeletonBox width="70px" height="12px" className="mt-1" />
      </div>
      <div className="flex flex-col items-center w-full">
        <div className="flex items-center w-full">
          <SkeletonBox width="12px" height="12px" className="rounded-full flex-shrink-0" />
          <SkeletonBox width="100%" height="2px" className="mx-2" />
          <SkeletonBox width="16px" height="16px" />
          <SkeletonBox width="100%" height="2px" className="mx-2" />
          <SkeletonBox width="12px" height="12px" className="rounded-full flex-shrink-0" />
        </div>
        <SkeletonBox width="60px" height="12px" className="mt-1" />
      </div>
      <div className="flex flex-col items-start gap-1 min-w-[90px]">
        <SkeletonBox width="50px" height="14px" />
        <SkeletonBox width="80px" height="12px" className="mt-1" />
        <SkeletonBox width="70px" height="12px" className="mt-1" />
      </div>
    </div>
  </div>
);

interface SearchResultsProps {
  flights: FlightDetails[];
  connectionFlights?: ConnectionFlight[];
  // stopsFilter: "none" | "one-or-fewer" | null;
  // onStopsFilterChange: (filter: "none" | "one-or-fewer" | null) => void;
  stopsFilter: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
  onStopsFilterChange: (filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null) => void;
  isLoadingFlights?: boolean;
  show: boolean;
  searchDate: string;
  selectedDates?: string[];
  onPostRequestSuccess?: () => void; // Add callback for successful post requests
}

const SearchResults: React.FC<SearchResultsProps> = ({ 
  flights, 
  connectionFlights = [], 
  stopsFilter, 
  onStopsFilterChange,
  isLoadingFlights = false,
  show, 
  searchDate, 
  selectedDates = [], 
  onPostRequestSuccess,
}) => {
  const { showToast } = useToastStore.getState();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [selectedFlights, setSelectedFlights] = useState<FlightDetails[]>([]);
  const [selectedConnections, setSelectedConnections] = useState<ConnectionFlight[]>([]);
  
  // State for individual segment selection
  interface SelectedSegment {
    segment: FlightSegment;
    connectionId: string; // Parent connection's compositeId
  }
  const [selectedSegments, setSelectedSegments] = useState<SelectedSegment[]>([]);
  
  const [isStopsDropdownOpen, setIsStopsDropdownOpen] = useState(false);
  const stopsDropdownRef = useRef<HTMLDivElement>(null);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (stopsDropdownRef.current && !stopsDropdownRef.current.contains(event.target as Node)) {
        setIsStopsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Only clear selected flights and segments when search params (not page) change
  const prevSearchParams = useRef({
    stopsFilter,
    searchDate,
    selectedDates: JSON.stringify(selectedDates),
  });

  useEffect(() => {
  const paramsChanged =
    prevSearchParams.current.stopsFilter !== stopsFilter ||
    prevSearchParams.current.searchDate !== searchDate ||
    prevSearchParams.current.selectedDates !==
      JSON.stringify(selectedDates);

  if (paramsChanged) {
    setSelectedFlights([]);
    setSelectedConnections([]);
    setSelectedSegments([]);
  }

  prevSearchParams.current = {
    stopsFilter,
    searchDate,
    selectedDates: JSON.stringify(selectedDates),
  };
}, [stopsFilter, searchDate, selectedDates]);

  const toggleFlightSelection = (flight: FlightDetails) => {
    const flightCompositeId = flight.compositeId || String(flight.id);
    const isSelected = selectedFlights.some((f) => {
      const fCompositeId = f.compositeId || String(f.id);
      return fCompositeId === flightCompositeId;
    });
    if (isSelected) {
      setSelectedFlights(selectedFlights.filter((f) => {
        const fCompositeId = f.compositeId || String(f.id);
        return fCompositeId !== flightCompositeId;
      }));
    } else {
      setSelectedFlights([...selectedFlights, flight]); // Add the new flight to the list
    }
  };

  // Helper: Check if a specific segment is selected
  const isSegmentSelected = (segment: FlightSegment, connectionId: string): boolean => {
    const segmentCompositeId = segment.compositeId || String(segment.id);
    return selectedSegments.some(
      (sel) => {
        const selSegmentId = sel.segment.compositeId || String(sel.segment.id);
        return selSegmentId === segmentCompositeId && sel.connectionId === connectionId;
      }
    );
  };

  // Helper: Check if all segments of a connection are selected
  const areAllSegmentsSelected = (connection: ConnectionFlight): boolean => {
    const connectionId = connection.compositeId || 
      `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;
    return connection.segments.every((segment) => isSegmentSelected(segment, connectionId));
  };

  // Toggle individual segment selection
  const toggleSegmentSelection = (segment: FlightSegment, connectionId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent parent click handlers
    }
    
    const segmentCompositeId = segment.compositeId || String(segment.id);
    const isSelected = isSegmentSelected(segment, connectionId);
    
    if (isSelected) {
      // Remove segment
      setSelectedSegments(selectedSegments.filter((sel) => {
        const selSegmentId = sel.segment.compositeId || String(sel.segment.id);
        return !(selSegmentId === segmentCompositeId && sel.connectionId === connectionId);
      }));
    } else {
      // Add segment
      setSelectedSegments([...selectedSegments, { segment, connectionId }]);
    }
  };

  // Toggle all segments in a connection (when clicking main card)
  const toggleAllSegmentsInConnection = (connection: ConnectionFlight) => {
    const connectionId = connection.compositeId || 
      `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;
    
    const allSelected = areAllSegmentsSelected(connection);
    
    if (allSelected) {
      // Deselect all segments of this connection
      setSelectedSegments(selectedSegments.filter((sel) => sel.connectionId !== connectionId));
    } else {
      // Select all segments of this connection
      const newSegments = connection.segments
        .filter((segment) => !isSegmentSelected(segment, connectionId))
        .map((segment) => ({ segment, connectionId }));
      setSelectedSegments([...selectedSegments, ...newSegments]);
    }
  };

  // Legacy function - kept for backward compatibility but now uses segment-based logic
  const toggleConnectionSelection = (connection: ConnectionFlight) => {
    toggleAllSegmentsInConnection(connection);
  };


  const handlePostRequest = async () => {
    if (selectedFlights.length === 0 && selectedSegments.length === 0) {
      showToast("Please select at least one flight to post a request.", "error", 3000);
      return;
    }

    const parseDate = (dateStr: string) => {
      try {
        const parts = dateStr?.split(" ");
        if (parts.length !== 3) throw new Error("Unexpected date format");

        const monthNames: { [key: string]: number } = {
          January: 1,
          February: 2,
          March: 3,
          April: 4,
          May: 5,
          June: 6,
          July: 7,
          August: 8,
          September: 9,
          October: 10,
          November: 11,
          December: 12,
        };

        const month = monthNames[parts[0]];
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (!month || isNaN(day) || isNaN(year)) throw new Error("Invalid date parts");

        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      } catch (error) {
        console.error("Error parsing date:", dateStr, error);
        return null;
      }
    };

    // Process direct flights
    const directFlightRequests = selectedFlights.map((flight) => {
      return {
        airlineId: flight.airlineId,
        flightNumber: flight.flightNumber,
        sourceCode: flight.departureCode,
        destinationCode: flight.arrivalCode,
        departureDate: new Date(`${flight.departureTime}Z`).toISOString(),
        arrvalDate: new Date(`${flight.arrivalTime}Z`).toISOString(),
        depterm: flight.depterminal || "N/A",
        arrterm: flight.arrterminal || "N/A",
        duration: flight.duration || "00:00",
        elptim: flight.elptim || "00:00",
        depcity: flight.depctry || "N/A",
        arrcity: flight.arrctry || "N/A",
      };
    });

    // Process individual selected segments - post each segment as a separate request
    const connectionFlightRequests = selectedSegments.map((selectedSeg) => {
      const segment = selectedSeg.segment;
      return {
        airlineId: segment.airlineId,
        flightNumber: segment.flightNumber,
        sourceCode: segment.departureCode,
        destinationCode: segment.arrivalCode,
        departureDate: new Date(`${segment.departureTime}Z`).toISOString(),
        arrvalDate: new Date(`${segment.arrivalTime}Z`).toISOString(),
        depterm: segment.depterminal || "N/A",
        arrterm: segment.arrterminal || "N/A",
        duration: segment.duration || "00:00",
        elptim: segment.duration || "00:00",
        depcity: segment.departureAirportName?.split(" ")[0] || "N/A",
        arrcity: segment.arrivalAirportName?.split(" ")[0] || "N/A",
      };
    });

    const requestData = {
      postRequest: [...directFlightRequests, ...connectionFlightRequests],
    };

    try {
      // console.log("Prepared request data for posting:", requestData);
      
      showLoader();
      const response = await postFlightRequest(requestData);
      console.log("Post request successful:", response);
      showToast("Flight request posted successfully!", "success", 3000);

      // Call the callback to refresh recent flights
      onPostRequestSuccess?.();
      setSelectedFlights([]);
      setSelectedConnections([]);
      setSelectedSegments([]); // Clear selected segments
      // fetchUserCreditBalance()
      setTimeout(() => {
    window.location.reload();
  }, 600);

    } catch (error: any) {
      console.error("Failed to post flight request:", error);
      const backendMessage = error?.response?.data?.message || error?.message || "Failed to post flight request. Please try again.";

      showToast(backendMessage, "error", 3000);
    } finally {
      hideLoader();
    }
  };

  // Render connection flight card
  const renderConnectionCard = (connection: ConnectionFlight, index: number) => {
    const connectionCompositeId = connection.compositeId || 
      `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;
    const allSegmentsSelected = areAllSegmentsSelected(connection);
    const [firstSegment, secondSegment] = connection.segments;

    // Render a single segment with checkbox
    const renderSegment = (segment: FlightSegment, segmentIndex: number) => {
      const isSegSelected = isSegmentSelected(segment, connectionCompositeId);
      
      return (
        <div
          key={segment.compositeId || `segment-${segment.id}-${segmentIndex}`}
          className={`rounded-lg p-3 mb-3 border cursor-pointer transition-all ${
            isSegSelected ? "border-[#FFD700] bg-yellow-50" : "border-gray-200 bg-white hover:bg-gray-50"
          }`}
          onClick={(e) => toggleSegmentSelection(segment, connectionCompositeId, e)}
        >
          {/* Segment Header with Checkbox */}
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={isSegSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleSegmentSelection(segment, connectionCompositeId);
                }}
                className="w-4 h-4 text-[#FFD700] border-gray-300 rounded focus:ring-[#FFD700] cursor-pointer"
              />
              <img
                className="w-10 h-10 object-contain rounded"
                src={segment.airlineLogo || DefaultAirLogo}
                alt={segment.airlineName}
                onError={(e) => {
                  e.currentTarget.src = DefaultAirLogo;
                }}
                loading="lazy"
              />
              <div>
                <p className="text-lightgray-200 text-xs">Airlines</p>
                <p className="text-grease-100 mt-1 text-xs font-medium">{segment.airlineName}</p>
              </div>
            </div>
            <div>
              <p className="text-lightgray-200 text-xs">Flight No.</p>
              <p className="text-grease-100 mt-1 text-xs font-medium">{segment.flightNumber}</p>
            </div>
          </div>

          {/* Segment Flight Details */}
          <div className="bg-lightblue-100 p-3 rounded-lg flex items-center gap-4">
            <div className="flex flex-col items-start gap-1 min-w-[80px]">
              <p className="text-grease-100 text-xs font-medium">{segment.departureCode}</p>
              <p className="text-lightgray-200 text-[10px]">{segment.departureAirportName?.split(" ")[0]}</p>
              <p className="text-lightgray-200 text-[10px]">
                {formatDateTime(segment.departureTime, {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </p>
            </div>

            <div className="flex flex-col items-center w-full">
              <div className="flex items-center w-full">
                <div className="w-2 h-2 bg-blue-100 rounded-full flex-shrink-0"></div>
                <div className="flex-1 border-b border-dashed border-indigo-200 mx-1"></div>
                <i className="st-flight-slim text-royalblue-100 text-xs mx-1" />
                <div className="flex-1 border-b border-dashed border-indigo-200 mx-1"></div>
                <div className="w-2 h-2 bg-blue-100 rounded-full flex-shrink-0"></div>
              </div>
              <p className="text-lightgray-150 text-[10px] text-center mt-1">
                {segment.duration ? `${parseInt(segment.duration.slice(0, 2), 10)}h ${segment.duration.slice(3, 5)}m` : ""}
              </p>
            </div>

            <div className="flex flex-col items-start gap-1 min-w-[80px]">
              <p className="text-grease-100 text-xs font-medium">{segment.arrivalCode}</p>
              <p className="text-lightgray-200 text-[10px]">{segment.arrivalAirportName?.split(" ")[0]}</p>
              <p className="text-lightgray-200 text-[10px]">
                {formatDateTime(segment.arrivalTime, {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </p>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div
        key={connectionCompositeId || `connection-${connection.segments[0].id}-${index}`}
        className={`bg-white rounded-lg p-5 border cursor-pointer ${
          allSegmentsSelected ? "border-[#FFD700] border-xl shadow-lg" : "border-gray-200"
        }`}
        onClick={() => toggleAllSegmentsInConnection(connection)}
      >
        {/* Header with via airport and Select All checkbox */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            {/* Select All Checkbox */}
            <input
              type="checkbox"
              checked={allSegmentsSelected}
              onChange={(e) => {
                e.stopPropagation();
                toggleAllSegmentsInConnection(connection);
              }}
              className="w-4 h-4 text-[#FFD700] border-gray-300 rounded focus:ring-[#FFD700] cursor-pointer flex-shrink-0 mt-1"
              title="Select all segments"
            />
            <div className="flex-1">
              <p className="text-sm text-grease-100 font-medium">
                via {connection.connectionAirportName || connection.connectionAirport}
              </p>
              <p className="text-xs text-lightgray-200 mt-1">
                {(() => {
                  const hours = Math.floor(connection.layoverMinutes / 60);
                  const minutes = connection.layoverMinutes % 60;
                  return hours > 0 
                    ? `${hours}h ${minutes}m layover`
                    : `${minutes}m layover`;
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* First Segment */}
        {renderSegment(firstSegment, 0)}

        {/* Layover indicator */}
        <div className="flex items-center justify-center my-2">
          <div className="flex items-center gap-2 px-3 py-1 bg-yellow-50 rounded-full">
            <i className="st-clock text-yellow-600 text-xs"></i>
            <p className="text-xs text-yellow-700 font-medium">
              {(() => {
                const hours = Math.floor(connection.layoverMinutes / 60);
                const minutes = connection.layoverMinutes % 60;
                return hours > 0 
                  ? `${hours}h ${minutes}m layover in ${connection.connectionAirport}`
                  : `${minutes}m layover in ${connection.connectionAirport}`;
              })()}
            </p>
          </div>
        </div>

        {/* Second Segment */}
        {renderSegment(secondSegment, 1)}

        {/* Total Duration */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-grease-100 text-center">
            Total duration: {connection.totalDuration}
          </p>
        </div>
      </div>
    );
  };

  const flightCards = useMemo(
    () => {
      const directCards = flights.map((flight, index) => {
        const flightCompositeId = flight.compositeId || String(flight.id);
        const isSelected = selectedFlights.some((f) => {
          const fCompositeId = f.compositeId || String(f.id);
          return fCompositeId === flightCompositeId;
        });
        return (
          <div
            key={flightCompositeId}
            className={`bg-white rounded-lg p-5 border cursor-pointer ${
              isSelected ? "border-[#FFD700] border-xl shadow-lg" : "border-gray-200"
            }`}
            onClick={() => toggleFlightSelection(flight)}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  className="w-12 h-12 object-contain rounded"
                  src={flight.airlineLogo || DefaultAirLogo}
                  alt={flight.airlineName}
                  onError={(e) => {
                    e.currentTarget.src = DefaultAirLogo;
                  }}
                  loading="lazy"
                />
                <div>
                  <p className="text-lightgray-200 text-xs">Airlines</p>
                  <p className="text-grease-100 mt-1 text-sm font-medium">{flight.airlineName}</p>
                </div>
              </div>
              <div>
                <p className="text-lightgray-200 text-xs">Flight No.</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">{flight.flightNumber}</p>
              </div>
            </div>
            <div className="bg-lightblue-100 p-4 rounded-lg mt-4 flex items-center gap-6">
              {/* LEFT SIDE */}
              <div className="flex flex-col items-start gap-1 min-w-[90px]">
                <p className="text-grease-100 text-sm font-medium">{flight.departureCode}</p>
                <p className="text-lightgray-200 mt-1 text-xs">{flight.departureAirportName?.split(" ")[0]}</p>

                <div className="flex items-center gap-1 mt-1">
                  <div className="rounded-full w-4 h-4 bg-gray-500 flex items-center justify-center">
                    <i className="st-top-right text-white text-[6px]" />
                  </div>
                  <p className="text-lightgray-200 text-xs">
                    {formatDateTime(flight.departureTime, {
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </p>
                </div>
              </div>

              {/* CENTER PATH */}
              <div className="flex flex-col items-center w-full">
                <div className="flex items-center w-full">
                  <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-royalblue-100 rounded-full" />
                    </div>
                  </div>

                  <div className="flex-1 border-b-2 border-dashed border-indigo-200 mx-2" />

                  <i className="st-flight-slim text-royalblue-100 text-base mx-1" />

                  <div className="flex-1 border-b-2 border-dashed border-indigo-200 mx-2" />

                  <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-royalblue-100 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* <p className="text-lightgray-150 text-xs text-center mt-1">
                  {`${parseInt(flight.duration.slice(0, 2), 10)}h ${flight.duration.slice(3, 5)}m`}
                </p> */}
                <p className="text-lightgray-150 text-xs text-center mt-1">
                  {flight?.elptim &&
                    `${parseInt(flight.elptim.slice(0, 3), 10)}h ${parseInt(
                      flight.elptim.slice(3, 5),
                      10
                    )}m`}
                </p>
                {flight?.stops > 0 && (
                  <p className="text-orange-500 text-xs mt-1">
                    {flight.stops} {flight.stops === 1 ? "Stop" : "Stops"}
                  </p>
                )}
              </div>

              {/* RIGHT SIDE */}
              <div className="flex flex-col items-start gap-1 min-w-[90px]">
                <p className="text-grease-100 text-sm font-medium">{flight.arrivalCode}</p>
                <p className="text-lightgray-200 mt-1 text-xs">{flight.arrivalAirportName?.split(" ")[0]}</p>

                <div className="flex items-center gap-1 mt-1">
                  <div className="rounded-full w-4 h-4 bg-gray-500 flex items-center justify-center">
                    <i className="st-bottom-right text-white text-[6px]" />
                  </div>
                  <p className="text-lightgray-200 text-xs">
                    {formatDateTime(flight.arrivalTime, {
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                    {flight.arrday && Number(flight.arrday) > 0 && (
                      <span className="ml-1 text-[10px] font-medium text-red-500">
                        +{flight.arrday}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      });

      const connectionCards = connectionFlights.map((connection, index) => 
        renderConnectionCard(connection, index)
      );

      return [...directCards, ...connectionCards];
    },
    [flights, connectionFlights, selectedFlights, selectedSegments]
  );

  const d = new Date(searchDate);
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const month = d.toLocaleDateString(undefined, { month: "long" });
  const day = d.getDate();
  const year = d.getFullYear();
  const formattedDate = `${month} ${day} ${year} - ${weekday}`;

  // Format selected dates for display
  const selectedDatesDisplay =
    selectedDates.length > 0
      ? selectedDates
          .map((date) => {
            const d = new Date(date);
            const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
            const month = d.toLocaleDateString(undefined, { month: "long" });
            const day = d.getDate();
            const year = d.getFullYear();
            return `${month} ${day} ${year} - ${weekday}`;
          })
          .join(", ")
      : formattedDate;

  if (!show) return null;
  
  // Show "No Data Found" only when not loading and no results
  const showNoData = !isLoadingFlights && flights.length === 0 && connectionFlights.length === 0;
  
  return (
    <section className="mt-6 searchResult">
      <div className="search-results mt-6">
        <div className="border-b pb-6 mb-4">
          <p className="text-grease-100 text-lg font-semibold">Search Results</p>
          <div className="flex items-center gap-2 mt-4 text-lightgray-200 text-sm">
            <i className="st-calendar text-base"></i>
            <span>{selectedDatesDisplay}</span>
          </div>
        </div>

        {/* Stops Filter */}
        <div className="relative mb-6" ref={stopsDropdownRef}>
          <button
            className="flex gap-2 p-3 border border-royalblue-100 rounded-lg hover:bg-lightblue-50"
            onClick={() => setIsStopsDropdownOpen(!isStopsDropdownOpen)}
          >
            <i className="st-filter text-royalblue-100" />
            <p className="font-semibold text-royalblue-100 text-xs">Stops</p>
          </button>

          {isStopsDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px]">
              <div className="p-4">
                <p className="text-grease-100 font-semibold text-sm mb-3">Stops</p>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="stops"
                      value="none"
                      checked={stopsFilter === "none"}
                      onChange={(e) => {
                        onStopsFilterChange("none");
                        setIsStopsDropdownOpen(false);
                      }}
                      className="w-4 h-4 text-royalblue-100 border-gray-300 focus:ring-royalblue-100"
                    />
                    <span className="text-sm text-grease-100">Non-stop only</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="stops"
                      value="1"
                      checked={stopsFilter === "one"}
                      onChange={(e) => {
                        onStopsFilterChange("one");
                        setIsStopsDropdownOpen(false);
                      }}
                      className="w-4 h-4 text-royalblue-100 border-gray-300 focus:ring-royalblue-100"
                    />
                    <span className="text-sm text-grease-100">1 Stop</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="stops"
                      value="1"
                      checked={stopsFilter === "two"}
                      onChange={(e) => {
                        onStopsFilterChange("two");
                        setIsStopsDropdownOpen(false);
                      }}
                      className="w-4 h-4 text-royalblue-100 border-gray-300 focus:ring-royalblue-100"
                    />
                    <span className="text-sm text-grease-100">2 Stops</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="stops"
                      value="1"
                      checked={stopsFilter === "three"}
                      onChange={(e) => {
                        onStopsFilterChange("three");
                        setIsStopsDropdownOpen(false);
                      }}
                      className="w-4 h-4 text-royalblue-100 border-gray-300 focus:ring-royalblue-100"
                    />
                    <span className="text-sm text-grease-100">3 Stops</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="stops"
                      value="one-or-fewer"
                      checked={stopsFilter === "one-or-fewer"}
                      onChange={(e) => {
                        onStopsFilterChange("one-or-fewer");
                        setIsStopsDropdownOpen(false);
                      }}
                      className="w-4 h-4 text-royalblue-100 border-gray-300 focus:ring-royalblue-100"
                    />
                    <span className="text-sm text-grease-100">Connected flights</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {isLoadingFlights ? (
          <div className="grid lg:grid-cols-3 sm:grid-cols-2 gap-6 mt-6">
            {[...Array(6)].map((_, index) => (
              <FlightCardSkeleton key={index} />
            ))}
          </div>
        ) : showNoData ? (
          <div className="flex flex-col items-center justify-center mt-12 mb-8">
            <div className="mb-6">
              <svg
                className="w-24 h-24 text-gray-300 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            {stopsFilter === "none" ? (
              <>
                <p className="text-center text-gray-700 text-lg font-semibold mb-3">
                  No direct flights found
                </p>
                <button
                  onClick={() => {
                    onStopsFilterChange("one-or-fewer");
                  }}
                  className="text-[#FFBC00] hover:text-[#e6a800] font-medium text-base underline cursor-pointer transition-colors"
                >
                  Search flights with one stop
                </button>
              </>
            ) : stopsFilter === "one-or-fewer" ? (
              <>
                <p className="text-center text-gray-700 text-lg font-semibold mb-3">
                  No one-stop flights found
                </p>
                <button
                  onClick={() => {
                    onStopsFilterChange("none");
                  }}
                  className="text-[#FFBC00] hover:text-[#e6a800] font-medium text-base underline cursor-pointer transition-colors"
                >
                  Try non-stop flights
                </button>
              </>
            ) : (
              <p className="text-center text-gray-500 text-lg mt-6">No Data Found</p>
            )}
          </div>
        ) : (
          <>
            <div className="grid lg:grid-cols-3 sm:grid-cols-2 gap-6 mt-6">{flightCards}</div>
            <div className="flex justify-center">
              <button
                className={`p-4 w-60 rounded-lg text-white text-center text-sm mt-6 ${
                  (selectedFlights.length > 0 || selectedSegments.length > 0) ? "bg-[#FFBC00] shadow-button" : "bg-gray-400 cursor-not-allowed"
                }`}
                onClick={handlePostRequest}
                disabled={selectedFlights.length === 0 && selectedSegments.length === 0}
              >
                POST {selectedFlights.length + selectedSegments.length} REQUEST{selectedFlights.length + selectedSegments.length !== 1 ? 'S' : ''}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default SearchResults;
